// E08-t4 (the wiring half) — MOUNTING THE ADMIN PAGE.   route: /:org/settings
//
// Contract: vault/Backlog/E08 Web App.md t4, whose own Notes are this file's
//   reason to exist: "App.tsx NEVER IMPORTS Admin.tsx — `/:org/settings` parses to
//   `admin` and PageOutlet falls through to an empty placeholder paragraph, so no
//   reader can reach this page in the running app, and admin.test.tsx renders
//   AdminPage directly, which is why nothing caught it."
// Contract: vault/Apps/Forge/Pages/Admin.md — the page's own features. This file
//   implements NONE of them: AdminPage is unchanged, and everything below is the
//   question of what may honestly be fed into it.
// Contract: web/src/pages/Admin.tsx — "the page takes DATA IN PROPS and the
//   container decides where it came from". This is that container.
// Contract: vault/Flows/Tenancy and Guards.md s2 — a refusal is an ordinary answer
//   with the guard named, so a non-member reaching this url reads the guard's own
//   sentence instead of an empty frame.
//
// ---------------------------------------------------------------------------
// WHAT IS REAL HERE, AND WHAT IS NOT — the page cannot say this, so the route does
// ---------------------------------------------------------------------------
// The Admin page is ORG-scoped and two of its three surfaces are KB-scoped, which
// is not a contradiction anybody has resolved yet: git-host serves usage and the
// command vocabulary per (org, kb) and serves NOTHING at org level except the
// caller's own session. So this container:
//
//   · picks ONE knowledge base of the org — the active one when the session has
//     one, otherwise the first the registry lists — and says WHICH, because a
//     guard matrix and a storage gauge are meaningless without their subject;
//   · shows the reader THEIR OWN membership as the members list, and says that it
//     is the only membership the control plane will disclose. A fabricated roster
//     is the exact failure E08 t4's row was written about;
//   · passes the PLATFORM DEFAULT guard matrix, which is what git-host/src/kb.ts
//     evaluates with today (`DEFAULT_GUARD_MATRIX.commands` plus the KB's own
//     protected branches) — and says that the branch half is the default rather
//     than this KB's, because no route serves the KB's list;
//   · reports every governance action as UNLANDED, because invite-member,
//     remove-member and set-guards have no endpoint. The page already offers the
//     controls to an admin; what it cannot know is that nothing receives them.
//
// None of that is hidden in a comment: each fact is on the page, next to the
// surface it qualifies, because an admin who cannot tell a real roster from a
// placeholder will govern with the placeholder.
//
// E14-t6 — the note, the refusal box and the "unlanded" strip were three
// hand-built boxes here; they are now Text and Banner. The gaps list keeps its
// own rule: it is a <ul> with an accessible name that admin-route.test.tsx reads
// by role/list, and Banner's children area is COLLAPSED by default — putting the
// qualifications inside one would hide, behind a click, the very sentences that
// stop an admin mistaking a placeholder for a roster.
import { useEffect, useMemo, useState } from 'react';

import { Banner } from '@astryxdesign/core/Banner';
import { Text } from '@astryxdesign/core/Text';

import { DEFAULT_GUARD_MATRIX, type Role } from '../../../platform/src/guards';
import type { AdminClient } from '../api/admin';
import { activeRole, type SessionPayload } from '../shell/AppShell';
import { AdminPage, type AdminCommand, type AdminMember, type UsageSurface } from './Admin';

import './admin-route.css';

export interface AdminRouteProps {
  /** the :org path segment — orgs.name */
  org: string;
  session: SessionPayload;
  client: AdminClient;
}

type Load =
  | { phase: 'loading' }
  | { phase: 'ready'; usage: UsageSurface; commands: AdminCommand[] }
  | { phase: 'error'; message: string };

/** Which KB this org's per-KB surfaces are read for. The ACTIVE one when the
 *  session has one in this org — a person opening settings from a KB expects to
 *  see that KB — otherwise the first the registry lists, deterministically. */
export function subjectKb(session: SessionPayload, org: string): string | null {
  const inOrg = session.kbs.filter((kb) => kb.org === org);
  const active = inOrg.find((kb) => kb.name === session.activeKb);
  return (active ?? inOrg[0])?.name ?? null;
}

export function AdminRoute({ org, session, client }: AdminRouteProps) {
  const kb = subjectKb(session, org);
  const [load, setLoad] = useState<Load>({ phase: 'loading' });
  /** what a governance control reported, and where it did NOT go */
  const [unlanded, setUnlanded] = useState<string | null>(null);

  useEffect(() => {
    if (kb === null) return;
    let live = true;
    setLoad({ phase: 'loading' });
    Promise.all([client.loadUsage(org, kb), client.loadCommands(org, kb)]).then(
      ([usage, commands]) => {
        if (live) setLoad({ phase: 'ready', usage, commands });
      },
      (err: unknown) => {
        if (live) setLoad({ phase: 'error', message: (err as Error)?.message ?? String(err) });
      },
    );
    return () => {
      live = false;
    };
  }, [client, org, kb]);

  // The role the control plane holds for this person in THIS org, and null when
  // they hold none — which AdminPage renders as the guard's own refusal.
  const role: Role | null = useMemo(
    () => activeRole({ ...session, activeOrg: org }),
    [session, org],
  );

  /** The one membership the control plane will disclose: the reader's own. */
  const members: AdminMember[] = useMemo(
    () =>
      role === null
        ? []
        : [{ userId: session.user.id, email: session.user.email, role }],
    [role, session.user.id, session.user.email],
  );

  const orgSummary = session.orgs.find((candidate) => candidate.name === org);

  if (kb === null) {
    return (
      <div className="dj-admin-route" data-page="admin">
        <Text as="p" color="secondary">
          The forge holds no knowledge base for <strong>{org}</strong> yet. The guard matrix and the
          storage gauges are per knowledge base, so there is nothing to show until one is created —
          <code> djc kb create</code> registers one.
        </Text>
      </div>
    );
  }

  if (load.phase === 'loading') {
    return (
      <div className="dj-admin-route" data-page="admin">
        <Text as="p" color="secondary" role="status">
          Loading {org}/{kb}…
        </Text>
      </div>
    );
  }

  if (load.phase === 'error') {
    return (
      <div className="dj-admin-route" data-page="admin">
        {/* the guard's own words, relayed — Tenancy s2. status="error" is what
            gives this role="alert", which is how admin-route.test.tsx reads it. */}
        <Banner status="error" container="card" title={load.message} />
      </div>
    );
  }

  return (
    <div className="dj-admin-route" data-page="admin">
      <AdminPage
        org={{ id: orgSummary?.id ?? org, name: org }}
        viewer={{ userId: session.user.id, email: session.user.email, role }}
        members={members}
        matrix={DEFAULT_GUARD_MATRIX}
        commands={load.commands}
        usage={load.usage}
        onGuardChange={(change) =>
          setUnlanded(
            `Nothing was written: the forge serves no set-guards route, so “${change.role} may ${change.command}” changed on this screen only.`,
          )
        }
        onInvite={(invite) =>
          setUnlanded(
            `Nothing was sent: the forge serves no invite-member route, so ${invite.email} was not invited.`,
          )
        }
        onRemoveMember={() =>
          setUnlanded(
            'Nothing was written: the forge serves no remove-member route, so no membership changed.',
          )
        }
      />

      {unlanded === null ? null : (
        <Banner status="warning" container="card" title={unlanded} role="status" />
      )}

      {/* The qualifications, next to the page they qualify. Each one names the
          surface that does not exist, so a reader can tell a real answer from a
          placeholder without reading this file. */}
      <ul className="dj-admin-route-gaps" aria-label="What this page cannot show yet">
        <li>
          Members lists your own membership only: the forge serves no org-level members route, so it
          cannot say who else is in {org}.
        </li>
        {/* E15-t2 — the KB's NAME is the url segment every per-KB read below is
            addressed by, so it is mono inside the sans sentence that qualifies
            it. It stays inside <strong>, which is carrying emphasis rather than
            a face. */}
        <li>
          The guard matrix and the storage below are for{' '}
          <strong>
            <Text type="code" color="inherit" weight="semibold">
              {kb}
            </Text>
          </strong>
          , the knowledge base this organization’s settings are read against.
        </li>
        <li>
          The matrix is the platform default that git-host evaluates with. Per-knowledge-base guard
          rules and protected branches are stored but served by no route, so an edit here is not
          this knowledge base’s stored rule.
        </li>
      </ul>
    </div>
  );
}

export default AdminRoute;
